import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import { LibraryBooks } from "@mui/icons-material";
import { uploadImage, clearMedia, uploadAudio } from "../../actions/mediaActions";
import {
  deleteChallenges,
  addEditChallenges,
} from "../../actions/challengeActions";
import { apiV2 } from "../../actions/index";
import SortableSessions from "./SortableSessions/SortableSessions";
import ImageContainerPreview from "../Common/ImageContainerPreview";
import LoadingBackdrop from "../Common/LoadingBackdrop";
import Swal from "sweetalert2";
import { preventEnterFormSubmission } from "../../utils/formUtils";
import AudioContainerPreview from "../Common/AudioContainerPreview";
import AuthorSelector from "../Common/AuthorSelector";
import UniversalLinkField from "../Common/UniversalLinkField";

const AddEditChallenges = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, setState] = useState({
    challenge_title: "",
    challenge_description: "",
    challenge_goal: "",
    challenge_is_active: 1,
    challenge_image: "",
    challenge_image_preview: "",
    challenge_image_wide: "",
    challenge_image_wide_preview: "",
    challenge_day_type: "",
    total_days: 0,
    challenge_certificate_image: null,
    challenge_certificate_image_preview: "",
    challenge_invite_image: null,
    challenge_invite_image_preview: "",
    challenge_reminder_image: null,
    challenge_reminder_image_preview: "",
    challenge_is_dark_mode: 0,
    challenge_author_id: 0,
    challenge_is_free: 0,
    original_challenge_is_free: 0,
    intro_session: {
      session_name: "",
      session_main_url: "",
      session_main_url_preview: "",
      session_image: "",
      session_image_preview: "",
      session_length: "",
      session_length_in_sec: "",
      challenge_day: "",
    },
    sessions: [],
    session_data: [],
    isSetToEdit: false,
    challenge_is_new: 0,
  });

  // Fetch challenge data on mount/ID change
  useEffect(() => {
    dispatch(clearMedia());

    if (isEdit) {
      apiV2.get(`/challenges/${selectedId}`).then((response) => {
        const currentChallenge = response.data;
        const session_data = currentChallenge.challenge_sessions || [];
        const sessions = session_data.map(obj => {
          let sessionsObj = {};
          sessionsObj[obj.session_id] = [obj.challenge_day, obj.session_repetition];
          return sessionsObj;
        });

        setState(prev => ({
          ...prev,
          ...currentChallenge,
          challenge_is_new: currentChallenge.challenge_is_new ?? 0,
          original_challenge_is_free: currentChallenge.challenge_is_free,
          session_data,
          sessions,
          isSetToEdit: true,
          intro_session: currentChallenge.intro_session || {
            session_name: "",
            session_main_url: "",
            session_main_url_preview: "",
            session_image: "",
            session_image_preview: "",
            session_length: "",
            session_length_in_sec: "",
            challenge_day: "",
          },
        }));
      });
    }
  }, [dispatch, isEdit, selectedId]);

  const handleFieldChange = useCallback(e => {
    const { name, value, type, checked } = e.target;
    setState(prev => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  }, []);

  const handleIntroFieldChange = useCallback(e => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      intro_session: {
        ...prev.intro_session,
        [name]: value,
      },
    }));
  }, []);

  const handleImageUpload = useCallback(
    e => {
      const file = e.target.files[0];
      if (state.challenge_image_preview) {
        window.URL.revokeObjectURL(state.challenge_image_preview);
      }
      setState(prev => ({
        ...prev,
        challenge_image_preview: window.URL.createObjectURL(file),
      }));
      dispatch(uploadImage(file))
        .then((response) => {
          setState(prev => ({
            ...prev,
            challenge_image: response.data.image_url,
          }));
          dispatch(clearMedia());
        });
    },
    [state.challenge_image_preview, dispatch]
  );

  const handleOtherImage = useCallback(
    e => {
      const { name } = e.target;
      const file = e.target.files[0];

      const imageTypes = {
        challenge_certificate_image: { preview: "challenge_certificate_image_preview", field: "challenge_certificate_image" },
        challenge_invite_image: { preview: "challenge_invite_image_preview", field: "challenge_invite_image" },
        challenge_reminder_image: { preview: "challenge_reminder_image_preview", field: "challenge_reminder_image" },
        challenge_image_wide: { preview: "challenge_image_wide_preview", field: "challenge_image_wide" },
        session_image: { preview: "intro_session.session_image_preview", field: "session_image" },
      };

      const imageType = imageTypes[name];
      if (!imageType) return;

      if (name === "session_image") {
        if (state.intro_session.session_image_preview) {
          window.URL.revokeObjectURL(state.intro_session.session_image_preview);
        }
        setState(prev => ({
          ...prev,
          intro_session: {
            ...prev.intro_session,
            session_image_preview: window.URL.createObjectURL(file),
          },
        }));
      } else {
        if (state[imageType.preview]) {
          window.URL.revokeObjectURL(state[imageType.preview]);
        }
        setState(prev => ({
          ...prev,
          [imageType.preview]: window.URL.createObjectURL(file),
        }));
      }
      dispatch(uploadImage(file))
        .then((response) => {
          const url = response.data.image_url;
          if (name === "session_image") {
            setState(prev => ({
              ...prev,
              intro_session: { ...prev.intro_session, session_image: url },
            }));
          } else {
            setState(prev => ({ ...prev, [imageType.field]: url }));
          }
          dispatch(clearMedia());
        });
    },
    [state, dispatch]
  );

  const handleBGMediaUpload = useCallback(
    e => {
      const file = e.target.files[0];
      if (!file) return;

      if (state.intro_session.session_main_url_preview) {
        window.URL.revokeObjectURL(state.intro_session.session_main_url_preview);
      }

      const objectURL = window.URL.createObjectURL(file);
      const mySound = new Audio([objectURL]);

      mySound.addEventListener(
        "canplaythrough",
        () => {
          URL.revokeObjectURL(objectURL);
          const minutes = Math.floor(mySound.duration / 60);
          const seconds = Math.round(mySound.duration - minutes * 60);
          setState(prev => ({
            ...prev,
            intro_session: {
              ...prev.intro_session,
              session_length: `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`,
              session_length_in_sec: Math.round(mySound.duration),
            },
          }));
        },
        false
      );

      setState(prev => ({
        ...prev,
        intro_session: {
          ...prev.intro_session,
          session_main_url_preview: window.URL.createObjectURL(file),
        },
      }));
      dispatch(uploadAudio(file))
        .then((response) => {
          setState(prev => ({
            ...prev,
            intro_session: {
              ...prev.intro_session,
              session_main_url: response.data.audio_file_url,
            },
          }));
          dispatch(clearMedia());
        });
    },
    [state.intro_session.session_main_url_preview, dispatch]
  );

  const handleChallengesDay = useCallback(e => {
    const { name, value } = e.target;
    const id = name.replace(/\D/g, "");

    setState(prev => ({
      ...prev,
      sessions: prev.sessions.map(obj => {
        if (Object.keys(obj)[0] === id) {
          let values = Object.values(obj)[0];
          let newObj = {};
          if (name.includes("day")) {
            values[0] = value;
          } else {
            values[1] = value;
          }
          newObj[id] = values;
          return newObj;
        }
        return obj;
      }),
    }));
  }, []);

  const submit = useCallback(() => {
    // Frontend validation
    const errors = [];
    if (!state.challenge_title?.trim()) errors.push("Challenge title is required");
    if (!state.challenge_goal?.trim()) errors.push("Challenge goal is required");
    if (!state.total_days || !Number.isInteger(Number(state.total_days))) errors.push("Total days must be a valid number");
    if (!state.intro_session?.session_name?.trim()) errors.push("Intro session name is required");

    if (errors.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        html: errors.map(e => `• ${e}`).join('<br/>'),
      });
      return;
    }

    Swal.fire({
      title: "Saving...",
      animation: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });

    const { sessions, ...challenge_json } = state;

    const options = {
      challenge_json: JSON.stringify(challenge_json),
      session_ids_json: JSON.stringify(sessions),
      intro_session_json: JSON.stringify(state.intro_session),
    };

    dispatch(addEditChallenges(options, () => navigate('/challenges')));
  }, [state, dispatch]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      submit();
    },
    [submit]
  );

  const handleDelete = useCallback(
    e => {
      e.preventDefault();
      Swal.fire({
        icon: "warning",
        text: "Are you sure you want to delete this challenge?",
        showConfirmButton: true,
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Delete Challenge",
      }).then(result => {
        if (result.value) {
          dispatch(deleteChallenges(state.challenge_id, () => navigate("/challenges")));
        }
      });
    },
    [state.challenge_id, dispatch, navigate]
  );

  useImperativeHandle(
    ref,
    () => ({
      submit,
    }),
    [submit]
  );

  useEffect(() => {
    console.log("State updated:", state);
  }, [state]);

  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open={true} />;
  }

  if (isEdit && !state.session_data) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <Box
      component="form"
      id="challenge-form"
      onSubmit={handleSubmit}
      onKeyDown={preventEnterFormSubmission}
      autoComplete="off"
    >
      <Box display="grid" gap={4}>
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
          <Box>
            <TextField
              fullWidth
              label="Challenge Name"
              name="challenge_title"
              value={state.challenge_title}
              onChange={handleFieldChange}
              required
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  name="challenge_is_active"
                  checked={state.challenge_is_active === 1}
                  onChange={e => setState(prev => ({ ...prev, challenge_is_active: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  name="challenge_is_new"
                  checked={!!state.challenge_is_new}
                  onChange={e => setState(prev => ({ ...prev, challenge_is_new: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Is New"
            />
            <FormControlLabel
              control={
                <Switch
                  name="challenge_is_free"
                  checked={state.challenge_is_free === 1}
                  onChange={e => setState(prev => ({ ...prev, challenge_is_free: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Is Free"
            />
            <FormControlLabel
              control={
                <Switch
                  name="challenge_is_dark_mode"
                  checked={state.challenge_is_dark_mode === 1}
                  onChange={e => setState(prev => ({ ...prev, challenge_is_dark_mode: e.target.checked ? 1 : 0 }))}
                />
              }
              label="Dark Mode"
            />
          </Box>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
          <Box>
            <TextField
              fullWidth
              label="Challenge Goal"
              name="challenge_goal"
              value={state.challenge_goal}
              onChange={handleFieldChange}
              required
            />
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={3}>
            <TextField
              fullWidth
              label="Duration (Days)"
              name="total_days"
              type="number"
              value={state.total_days}
              onChange={handleFieldChange}
              InputProps={{ inputProps: { min: 0 } }}
            />
            <FormControl fullWidth>
              <InputLabel id="day-type-label">Day Type</InputLabel>
              <Select
                labelId="day-type-label"
                name="challenge_day_type"
                value={state.challenge_day_type}
                label="Day Type"
                onChange={handleFieldChange}
              >
                <MenuItem value="DAY">DAY</MenuItem>
                <MenuItem value="WEEK">WEEK</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
          <Box>
            <AuthorSelector
              label="Challenge Author"
              value={state.challenge_author_id}
              onChange={authorId => setState(prev => ({ ...prev, challenge_author_id: authorId }))}
              fullWidth
            />
          </Box>
        </Box>

        <Box>
          <TextField
            fullWidth
            label="Description"
            name="challenge_description"
            value={state.challenge_description}
            onChange={handleFieldChange}
            multiline
            rows={2}
          />
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }}
          gap={4}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Reminder Image
            </Typography>
            <ImageContainerPreview
              title="Reminder Image"
              image={state.challenge_reminder_image_preview || state.challenge_reminder_image}
              name="challenge_reminder_image"
              handleRemove={() =>
                setState(prev => ({ ...prev, challenge_reminder_image: "", challenge_reminder_image_preview: null }))
              }
              handleImageUpload={handleOtherImage}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
            />
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Certificate Image
            </Typography>
            <ImageContainerPreview
              title="Certificate Image"
              image={state.challenge_certificate_image_preview || state.challenge_certificate_image}
              name="challenge_certificate_image"
              handleRemove={() =>
                setState(prev => ({
                  ...prev,
                  challenge_certificate_image: "",
                  challenge_certificate_image_preview: null,
                }))
              }
              handleImageUpload={handleOtherImage}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
            />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Challenge Image
            </Typography>
            <ImageContainerPreview
              title="Challenge Image"
              image={state.challenge_image_preview || state.challenge_image}
              name="challenge_image"
              handleRemove={() => setState(prev => ({ ...prev, challenge_image: "", challenge_image_preview: null }))}
              handleImageUpload={handleImageUpload}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
            />
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Challenge Wide Image
            </Typography>
            <ImageContainerPreview
              title="Challenge Wide Image"
              image={state.challenge_image_wide_preview || state.challenge_image_wide}
              name="challenge_image_wide"
              handleRemove={() =>
                setState(prev => ({ ...prev, challenge_image_wide: "", challenge_image_wide_preview: null }))
              }
              handleImageUpload={handleOtherImage}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
            />
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Invitation Image
            </Typography>
            <ImageContainerPreview
              title="Invitation Image"
              image={state.challenge_invite_image_preview || state.challenge_invite_image}
              name="challenge_invite_image"
              handleRemove={() =>
                setState(prev => ({ ...prev, challenge_invite_image: "", challenge_invite_image_preview: null }))
              }
              handleImageUpload={handleOtherImage}
              showCopyUrlButton={true}
              sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
            />
          </Box>
        </Box>

        <Box>
          <UniversalLinkField url={state.universal_link} />
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>
            Intro Session
          </Typography>
          <Box display="grid" gap={3}>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(2, 1fr)" }}
              sx={{ height: "100%" }}
              gap={4}
            >
              <Box display="grid" gap={3} sx={{ pt: 4 }}>
                <TextField
                  fullWidth
                  label="Intro Title"
                  name="session_name"
                  value={state.intro_session.session_name}
                  onChange={handleIntroFieldChange}
                  required
                />
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Intro Audio
                  </Typography>
                  <AudioContainerPreview
                    title="Intro Audio"
                    audio={state.intro_session.session_main_url_preview || state.intro_session.session_main_url}
                    name="intro_audio"
                    sx={{ height: "100%" }}
                    handleRemove={() =>
                      setState(prev => ({
                        ...prev,
                        intro_session: {
                          ...prev.intro_session,
                          session_main_url: "",
                          session_main_url_preview: null,
                          session_length: "",
                          session_length_in_sec: "",
                        },
                      }))
                    }
                    handleAudioUpload={handleBGMediaUpload}
                    showCopyUrlButton={true}
                    onLoadedData={e => {
                      const minutes = Math.floor(e.target.duration / 60);
                      const seconds = Math.round(e.target.duration - minutes * 60);
                      setState(prev => ({
                        ...prev,
                        intro_session: {
                          ...prev.intro_session,
                          session_length: `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`,
                          session_length_in_sec: Math.round(e.target.duration),
                        },
                      }));
                    }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Intro Image
                </Typography>
                <ImageContainerPreview
                  title="Intro Image"
                  image={state.intro_session.session_image_preview || state.intro_session.session_image}
                  name="session_image"
                  handleRemove={() =>
                    setState(prev => ({
                      ...prev,
                      intro_session: {
                        ...prev.intro_session,
                        session_image: "",
                        session_image_preview: null,
                      },
                    }))
                  }
                  handleImageUpload={handleOtherImage}
                  showCopyUrlButton={true}
                  sx={{ padding: 1, background: "#FFFCF5", height: 90, objectFit: "contain" }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {isEdit && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LibraryBooks /> Sessions
            </Typography>
            <Paper sx={{ p: 2 }}>
              <SortableSessions
                items={state.sessions || []}
                sessions={state.session_data || []}
                onSortEnd={({ oldIndex, newIndex }) => {
                  const newSessions = [...state.sessions];
                  const [removed] = newSessions.splice(oldIndex, 1);
                  newSessions.splice(newIndex, 0, removed);
                  setState(prev => ({ ...prev, sessions: newSessions }));
                }}
                handleChallengesDay={handleChallengesDay}
                state={state}
              />
            </Paper>
          </Box>
        )}

        {isEdit && (
          <Box sx={{ textAlign: "right" }}>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Delete Challenge
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
});

AddEditChallenges.displayName = "AddEditChallenges";

export default AddEditChallenges;
